// frontend/admin/exams/index.js
// Updated version with correct initialization order

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import VisibilityIcon from "@mui/icons-material/Visibility";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useTranslation } from "hooks/useTranslation";

import {
  getStudentExams,
  getAnnotators,
  getAllAnnotatorsByAdmin,
  getAllAnnotatorsByAdminIncludingDeleted,
  getAllAnnotatorsIncludingDeleted,
  assignStudentExams,
  getAdmins,
} from "services/sampleService";
import { getUserInfo } from "services/authService";

// Status options with translation support
const getStatusOptions = (t) => [
  "",
  t("exams.statusOptions.pending", "Pending"),
  t("exams.statusOptions.inProgress", "In Progress"),
  t("exams.statusOptions.annotatedOnce", "Annotated Once"),
  t("exams.statusOptions.annotatedTwice", "Annotated Twice"),
];

/* ----------------------- */
/*   Helper UI Components  */
/* ----------------------- */

function SelectAllHeader({ rowsData, selectedIds, setSelectedIds, t }) {
  const allIds = rowsData.map((r) => r._row_id);

  // Only check IDs that exist in current data
  const currentSelectedIds = selectedIds.filter((id) => allIds.includes(id));

  const checked = rowsData.length > 0 && currentSelectedIds.length === rowsData.length;
  const indeterminate =
    currentSelectedIds.length > 0 && currentSelectedIds.length < rowsData.length;

  const onChange = (e) => {
    if (e.target.checked) {
      // Add all current IDs to selection
      const newSelectedIds = [...new Set([...selectedIds, ...allIds])];
      setSelectedIds(newSelectedIds);
    } else {
      // Remove only current IDs from selection
      const newSelectedIds = selectedIds.filter((id) => !allIds.includes(id));
      setSelectedIds(newSelectedIds);
    }
  };

  return (
    <Checkbox
      indeterminate={indeterminate}
      checked={checked}
      onChange={onChange}
      inputProps={{ "aria-label": t ? t("exams.table.selectAll", "Select All") : "Select All" }}
    />
  );
}

SelectAllHeader.propTypes = {
  rowsData: PropTypes.arrayOf(
    PropTypes.shape({
      _row_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
    .isRequired,
  setSelectedIds: PropTypes.func.isRequired,
  t: PropTypes.func,
};

SelectAllHeader.defaultProps = {
  t: null,
};

function RowSelectCell({ row, selectedIds, setSelectedIds }) {
  const rowId = row?.original?._row_id;
  const isChecked = selectedIds.includes(rowId);

  const onChange = (e) => {
    setSelectedIds((prev) =>
      e.target.checked ? [...prev, rowId] : prev.filter((x) => x !== rowId)
    );
  };

  return <Checkbox checked={isChecked} onChange={onChange} />;
}

RowSelectCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      _row_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
  }).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
    .isRequired,
  setSelectedIds: PropTypes.func.isRequired,
};

// Helper component to display Annotator details + per-annotator View Sample button
function AnnotatorDetails({ annotator, type, examId, annotatorSlot }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!annotator || !annotator.id || annotator.id === 0) {
    return (
      <MDTypography variant="caption" color="text.secondary">
        {t("exams.table.notAssigned", "Not Assigned")}
      </MDTypography>
    );
  }

  const status = String(annotator.status || "").toLowerCase();
  const hasSubmitted = status === "submitted" || status === "completed";

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {annotator.name || `Annotator ${annotator.id}`}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
        <Typography variant="caption" color="text.secondary">
          {annotator.status}
        </Typography>
        {hasSubmitted && examId && (
          <MDButton
            size="small"
            color="info"
            variant="text"
            onClick={() =>
              navigate(
                `/admin/exams/view-sample?exam_id=${encodeURIComponent(
                  examId
                )}&annotator=${annotatorSlot}`
              )
            }
            sx={{ py: 0, px: 0.5, minHeight: 0, fontSize: "0.7rem", lineHeight: 1.5 }}
          >
            {t("exams.buttons.view", "View")}
          </MDButton>
        )}
      </Box>
    </Box>
  );
}

AnnotatorDetails.propTypes = {
  annotator: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    assigned_on: PropTypes.string,
    assigned_by: PropTypes.number,
    assigned_by_name: PropTypes.string,
    status: PropTypes.string,
  }),
  type: PropTypes.oneOf(["A1", "A2"]).isRequired,
  examId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  annotatorSlot: PropTypes.oneOf([1, 2]).isRequired,
};

AnnotatorDetails.defaultProps = {
  annotator: null,
  examId: "",
};

// Custom cell components with PropTypes
function Annotator1DetailsCell({ row }) {
  return (
    <AnnotatorDetails
      annotator={row.original.annotator1}
      type="A1"
      examId={row.original.exam_id}
      annotatorSlot={1}
    />
  );
}

Annotator1DetailsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      annotator1: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        assigned_on: PropTypes.string,
        assigned_by: PropTypes.number,
        assigned_by_name: PropTypes.string,
        status: PropTypes.string,
      }),
      exam_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
  }).isRequired,
};

function Annotator2DetailsCell({ row }) {
  return (
    <AnnotatorDetails
      annotator={row.original.annotator2}
      type="A2"
      examId={row.original.exam_id}
      annotatorSlot={2}
    />
  );
}

Annotator2DetailsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      annotator2: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        assigned_on: PropTypes.string,
        assigned_by: PropTypes.number,
        assigned_by_name: PropTypes.string,
        status: PropTypes.string,
      }),
      exam_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
  }).isRequired,
};

function OverallStatusCell({ value }) {
  const getColor = (status) => {
    switch (status) {
      case "Pending":
        return "warning";
      case "In Progress":
        return "info";
      case "Annotated Once":
        return "primary";
      case "Annotated Twice":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Chip
      label={value}
      size="small"
      color={getColor(value)}
      sx={{ fontWeight: 500, fontSize: "0.7rem" }}
    />
  );
}

OverallStatusCell.propTypes = {
  value: PropTypes.string.isRequired,
};

/* ----------------------- */
/*        Main Page        */
/* ----------------------- */

function ExamsIndex() {
  const { t } = useTranslation();
  const [userInfo, setUserInfo] = useState(null);

  // Initialize filters with user info if available immediately
  const initialUser = getUserInfo();

  // Persisted filters (sessionStorage) so navigating to View Sample and back keeps the selection.
  const EXAMS_FILTERS_KEY = "admin.exams.filters";
  const defaultFilters = {
    status: "Pending",
    exam_id: "",
    annotator1_id: "",
    annotator2_id: "",
    pending_assignment: false,
    page: 1,
    limit: 25,
  };
  const loadStoredFilters = () => {
    try {
      const raw = sessionStorage.getItem(EXAMS_FILTERS_KEY);
      if (!raw) return defaultFilters;
      const parsed = JSON.parse(raw);
      return { ...defaultFilters, ...(parsed && typeof parsed === "object" ? parsed : {}) };
    } catch (e) {
      return defaultFilters;
    }
  };
  const [filters, setFilters] = useState(loadStoredFilters);

  // Sync filters → sessionStorage on every change
  useEffect(() => {
    try {
      sessionStorage.setItem(EXAMS_FILTERS_KEY, JSON.stringify(filters));
    } catch (e) {
      // ignore quota errors
    }
  }, [filters]);

  const [loading, setLoading] = useState(false);
  const [rowsData, setRowsData] = useState([]);
  const [total, setTotal] = useState(0);

  // selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [annotators, setAnnotators] = useState([]);
  const [annotatorsDel, setAnnotatorsDel] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [assignA1, setAssignA1] = useState("");
  const [assignA2, setAssignA2] = useState("");

  // snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    color: "success",
    title: "",
    message: "",
  });

  const showSnackbar = ({ color = "success", title = "", message = "", duration = 4000 }) => {
    setSnackbar({ open: true, color, title, message });

    setTimeout(() => {
      setSnackbar((s) => ({ ...s, open: false }));
    }, duration);
  };

  const closeSnackbar = () => {
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const statusOptions = getStatusOptions(t);

  // Get current user info
  useEffect(() => {
    const user = getUserInfo();
    setUserInfo(user);
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: (
          <SelectAllHeader
            rowsData={rowsData}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
            t={t}
          />
        ),
        accessor: "_select",
        align: "center",
        Cell: (cellProps) => (
          <RowSelectCell
            row={cellProps.row}
            selectedIds={selectedIds}
            setSelectedIds={setSelectedIds}
          />
        ),
        width: "5%",
      },
      {
        Header: t("exams.table.sessionId", "Sample ID"),
        accessor: "exam_id",
        align: "left",
      },
      {
        Header: t("exams.table.examDate", "Date"),
        accessor: "exam_date",
        align: "left",
      },
      {
        Header: t("exams.filters.annotator1Details", "Annotator 1 Details"),
        accessor: "annotator1_details",
        align: "left",
        Cell: Annotator1DetailsCell,
      },
      {
        Header: t("exams.filters.annotator2Details", "Annotator 2 Details"),
        accessor: "annotator2_details",
        align: "left",
        Cell: Annotator2DetailsCell,
      },
      {
        Header: t("exams.table.overallStatus", "Overall Status"),
        accessor: "overall_status",
        align: "left",
        Cell: OverallStatusCell,
      },
    ],
    [rowsData, selectedIds, t]
  );

  const fetchExams = async (useFilters = filters) => {
    try {
      setLoading(true);

      // Prepare filters for API call
      const apiFilters = { ...useFilters };

      console.log("Sending filters to API:", apiFilters);
      //console.log("Admin filter value:", apiFilters.assigned_by_admin);

      const resp = await getStudentExams(apiFilters);

      const mapped = (resp.data || []).map((e) => {
        // Create annotator objects with all details
        const annotator1 = {
          id: e.annotator1_id,
          name: e.annotator1_name,
          assigned_on: e.annotator1_assigned_on,
          assigned_by: e.annotator1_assigned_by,
          assigned_by_name: e.annotator1_assigned_by_name || `ID: ${e.annotator1_assigned_by}`,
          status: e.annotator1_status,
        };

        const annotator2 = {
          id: e.annotator2_id,
          name: e.annotator2_name,
          assigned_on: e.annotator2_assigned_on,
          assigned_by: e.annotator2_assigned_by,
          assigned_by_name: e.annotator2_assigned_by_name || `ID: ${e.annotator2_assigned_by}`,
          status: e.annotator2_status,
        };

        return {
          _row_id: e.id ?? e.exam_id,
          exam_id: e.exam_id ?? "",
          exam_date: e.start_time ? new Date(e.start_time).toLocaleDateString() : "",
          annotator1,
          annotator2,
          // Add searchable string fields for annotator details
          annotator1_details: annotator1.name || `ID: ${annotator1.id}` || "Not Assigned",
          annotator2_details: annotator2.name || `ID: ${annotator2.id}` || "Not Assigned",
          overall_status: e.status ?? "Pending",
          originalData: e,
        };
      });

      setRowsData(mapped);
      setTotal(resp.total_records ?? mapped.length);
      setSelectedIds([]); // Clear selections when data changes
    } catch (err) {
      console.error("fetchExams error:", err);
      setRowsData([]);
      setTotal(0);
      setSelectedIds([]);
      showSnackbar({
        color: "error",
        title: t("exams.notifications.fetchError", "Fetch Error"),
        message: t("exams.notifications.fetchErrorMessage", "Failed to load exams data"),
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnotators = async () => {
    try {
      //const list = await getAnnotators();
      //setAnnotators(list);
      const userInfo = getUserInfo();
      let response, response2;

      let userRole = userInfo.role;

      if (userRole === "SuperAdmin") {
        // Superadmin: Get all annotators (active for assignment)
        response = await getAnnotators();
        setAnnotators(response);
        // Superadmin: Get all annotators including deleted (for filter dropdowns)
        response2 = await getAllAnnotatorsIncludingDeleted();
        setAnnotatorsDel(response2);
      } else {
        // Regular admin: Get annotators created by this admin
        const adminId = userInfo?.id;
        response = await getAllAnnotatorsByAdmin(adminId);
        setAnnotators(response);
        response2 = await getAllAnnotatorsByAdminIncludingDeleted(adminId);
        setAnnotatorsDel(response2);
      }
    } catch (err) {
      console.error("fetchAnnotators error:", err);
      setAnnotators([]);
      showSnackbar({
        color: "error",
        title: t("exams.notifications.fetchError", "Fetch Error"),
        message: t("exams.notifications.annotatorsFetchError", "Failed to load annotators"),
      });
    }
  };

  function formatAssignmentIssues(errors = {}) {
    const issueMap = {
      annotator1_already_assigned: "Annotator 1 already assigned",
      annotator2_already_assigned: "Annotator 2 already assigned",
    };

    return Object.entries(errors)
      .filter(([, count]) => count > 0)
      .map(([key, count]) => `• ${issueMap[key] || key}: ${count} exam${count > 1 ? "s" : ""}`)
      .join("\n");
  }

  useEffect(() => {
    const init = async () => {
      await fetchAnnotators();
      // Pass the current filters to fetchExams to ensure admin filter is applied
      await fetchExams(filters);
    };
    init();
  }, []);

  // Re-fetch exams when filters change (except initial load)
  useEffect(() => {
    if (userInfo) {
      // Only refetch if filters have changed (not on initial userInfo set)
      fetchExams();
    }
  }, [
    userInfo,
    filters.status,
    filters.exam_id,
    filters.annotator1_id,
    filters.annotator2_id,
    //filters.assigned_by_admin,
    filters.pending_assignment,
    filters.page,
  ]);

  const onApply = () => {
    const next = { ...filters, page: 1 };
    setFilters(next);
    fetchExams(next);
  };

  const onReset = () => {
    // When resetting, check if user is admin and restore their filter
    const reset = {
      status: "Pending",
      exam_id: "",
      annotator1_id: "",
      annotator2_id: "",
      //assigned_by_admin: userInfo?.role === "Admin" && userInfo?.id ? userInfo.id : "",
      pending_assignment: false,
      page: 1,
      limit: 25,
    };
    setFilters(reset);
    fetchExams(reset);
  };

  const onPageChange = (newPage) => {
    const next = { ...filters, page: newPage };
    setFilters(next);
    fetchExams(next);
  };

  const onAssign = async () => {
    if (selectedIds.length === 0) {
      showSnackbar({
        color: "error",
        title: t("exams.notifications.noSelection", "No Selection"),
        message: t("exams.notifications.noSelectionMessage", "Please select exams to assign"),
      });
      return;
    }

    // Filter selectedIds to only include those that exist in current rowsData
    const currentRowIds = rowsData.map((r) => r._row_id);
    const validSelectedIds = selectedIds.filter((id) => currentRowIds.includes(id));

    if (validSelectedIds.length === 0) {
      showSnackbar({
        color: "error",
        title: t("exams.notifications.invalidSelection", "Invalid Selection"),
        message: t(
          "exams.notifications.invalidSelectionMessage",
          "Selected exams are no longer in the current view"
        ),
      });
      setSelectedIds([]);
      return;
    }

    const exam_ids = validSelectedIds;

    const payload = {
      exam_ids,
      annotator1_id: assignA1 || undefined,
      annotator2_id: assignA2 || undefined,
      assigned_by: userInfo?.id || undefined,
    };

    try {
      setLoading(true);

      const resp = await assignStudentExams(payload);

      const isWarning = resp?.status === "warning";

      const issueDetails =
        isWarning && resp?.errors ? "\n\n" + formatAssignmentIssues(resp.errors) : "";

      showSnackbar({
        color: isWarning ? "warning" : "success",
        title: isWarning
          ? t("exams.notifications.assignmentIssues", "Assignment Issues Found")
          : t("exams.notifications.assignmentSuccess", "Assignment Successful"),
        message: `${resp.message}${issueDetails}`,
      });

      if (resp?.assigned > 0) {
        setAssignA1("");
        setAssignA2("");
        await fetchExams();
      }
    } catch (err) {
      console.error("Assign error:", err);

      // ✅ FIXED
      const apiError = err;

      showSnackbar({
        color: "error",
        title: t("exams.notifications.assignmentFailed", "Assignment Failed"),
        message:
          apiError?.message ||
          t("exams.notifications.assignmentFailedMessage", "Failed to assign annotators"),
      });
    } finally {
      setLoading(false);
    }
  };

  const assignmentBarVisible = selectedIds.length > 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Filters */}
            <Card sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", boxShadow: 2 }}>
              <MDBox display="flex" flexWrap="wrap" alignItems="center" gap={2}>
                {/* Status Dropdown */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel
                    id="status-label"
                    shrink={true}
                    sx={{
                      backgroundColor: "background.paper",
                      px: 1,
                      transform: "translate(14px, -16px) scale(0.75)",
                    }}
                  >
                    {t("exams.filters.status", "Status")}
                  </InputLabel>
                  <Select
                    labelId="status-label"
                    value={filters.status}
                    onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                    label={t("exams.filters.status", "Status")}
                    displayEmpty
                    sx={{
                      height: "56px",
                      "& .MuiSelect-select": {
                        paddingTop: "14px",
                        paddingBottom: "14px",
                      },
                    }}
                  >
                    {statusOptions.map((s) => (
                      <MenuItem key={s || "any"} value={s}>
                        {s || t("exams.filters.any", "Any")}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Exam ID field */}
                <TextField
                  label={t("exams.table.sessionId", "Sample ID")}
                  size="small"
                  value={filters.exam_id}
                  onChange={(e) => setFilters({ ...filters, exam_id: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      onApply();
                    }
                  }}
                  sx={{
                    minWidth: 250,
                    height: "56px",
                  }}
                  InputProps={{
                    sx: {
                      height: "56px",
                    },
                  }}
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      backgroundColor: "background.paper",
                      px: 1,
                    },
                  }}
                />

                {/* Annotator 1 Dropdown - Single Select */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel
                    id="annotator1-label"
                    shrink={true}
                    sx={{
                      backgroundColor: "background.paper",
                      px: 1,
                      transform: "translate(14px, -16px) scale(0.75)",
                    }}
                  >
                    {t("exams.filters.annotator1", "Annotator 1")}
                  </InputLabel>

                  <Select
                    labelId="annotator1-label"
                    value={filters.annotator1_id}
                    displayEmpty
                    onChange={(e) =>
                      setFilters({ ...filters, annotator1_id: e.target.value, page: 1 })
                    }
                    label={t("exams.filters.annotator1", "Annotator 1")}
                    sx={{
                      height: "56px",
                      "& .MuiSelect-select": {
                        paddingTop: "14px",
                        paddingBottom: "8px",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("exams.filters.all", "All")}</em>
                    </MenuItem>

                    {/* All annotators including deleted (for both SuperAdmin and Admin) */}
                    {annotatorsDel.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name}
                        {a.is_deleted && " (Deleted)"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Annotator 2 Dropdown - Single Select */}
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel
                    id="annotator2-label"
                    shrink={true}
                    sx={{
                      backgroundColor: "background.paper",
                      px: 1,
                      transform: "translate(14px, -16px) scale(0.75)",
                    }}
                  >
                    {t("exams.filters.annotator2", "Annotator 2")}
                  </InputLabel>

                  <Select
                    labelId="annotator2-label"
                    value={filters.annotator2_id}
                    displayEmpty
                    onChange={(e) =>
                      setFilters({ ...filters, annotator2_id: e.target.value, page: 1 })
                    }
                    label={t("exams.filters.annotator2", "Annotator 2")}
                    sx={{
                      height: "56px",
                      "& .MuiSelect-select": {
                        paddingTop: "14px",
                        paddingBottom: "8px",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>{t("exams.filters.all", "All")}</em>
                    </MenuItem>

                    {/* ✅ All annotators including deleted (for both SuperAdmin and Admin) */}
                    {annotatorsDel.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {a.name}
                        {a.is_deleted && " (Deleted)"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Apply and Reset buttons */}
                <MDButton color="secondary" variant="outlined" onClick={onReset} disabled={loading}>
                  {t("exams.buttons.reset", "Reset")}
                </MDButton>
              </MDBox>
            </Card>

            {/* Assignment Bar */}
            {assignmentBarVisible && (
              <Card sx={{ p: 2, mb: 2, border: "1px solid #e0e0e0", boxShadow: 1 }}>
                <MDBox
                  display="flex"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={2}
                >
                  <MDTypography variant="button">
                    {t("exams.assignment.selectedCount", "Selected:")}{" "}
                    <strong>{selectedIds.length}</strong>
                  </MDTypography>

                  <MDBox display="flex" gap={2} flexWrap="wrap" alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel
                        id="assign-a1-label"
                        shrink={true}
                        sx={{
                          backgroundColor: "background.paper",
                          px: 1,
                          transform: "translate(14px, -9px) scale(0.75)",
                        }}
                      >
                        {t("exams.assignment.annotator1", "Annotator 1")}
                      </InputLabel>
                      <Select
                        labelId="assign-a1-label"
                        value={assignA1}
                        onChange={(e) => setAssignA1(e.target.value)}
                        label={t("exams.assignment.annotator1", "Annotator 1")}
                        sx={{
                          "& .MuiSelect-select": {
                            paddingTop: "14px",
                            paddingBottom: "8px",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>{t("exams.table.none", "None")}</em>
                        </MenuItem>
                        {annotators.map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel
                        id="assign-a2-label"
                        shrink={true}
                        sx={{
                          backgroundColor: "background.paper",
                          px: 1,
                          transform: "translate(14px, -9px) scale(0.75)",
                        }}
                      >
                        {t("exams.assignment.annotator2", "Annotator 2")}
                      </InputLabel>
                      <Select
                        labelId="assign-a2-label"
                        value={assignA2}
                        onChange={(e) => setAssignA2(e.target.value)}
                        label={t("exams.assignment.annotator2", "Annotator 2")}
                        sx={{
                          "& .MuiSelect-select": {
                            paddingTop: "14px",
                            paddingBottom: "8px",
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>{t("exams.table.none", "None")}</em>
                        </MenuItem>
                        {annotators.map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <MDButton
                      color="info"
                      onClick={onAssign}
                      disabled={loading || (assignA1 === "" && assignA2 === "")}
                    >
                      {loading
                        ? t("exams.buttons.assigning", "Assigning...")
                        : t("exams.buttons.assign", "Assign")}
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            )}

            {/* Table - Fixed entries per page to 25 */}
            <Card
              sx={{
                boxShadow: 4,
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: "#f9fafe",
                border: "1px solid #e0e0e0",
              }}
            >
              <MDBox>
                <DataTable
                  table={{ columns, rows: rowsData }}
                  isSorted
                  canSearch
                  showTotalEntries
                  totalRecords={total}
                  entriesPerPage={{
                    defaultValue: 25,
                    entries: [25, 50, 100, 250],
                    onChange: (value) => {
                      const next = { ...filters, limit: value, page: 1 };
                      setFilters(next);
                      fetchExams(next);
                    },
                  }}
                  pagination={{
                    variant: "gradient",
                    color: "info",
                    page: filters.page - 1,
                    onPageChange: (_, newPage) => onPageChange(newPage + 1),
                  }}
                  noEndBorder={false}
                />
              </MDBox>
            </Card>

            <MDBox mt={1} textAlign="right" color="text">
              <MDTypography variant="caption">
                {t("exams.table.totalRecords", "Total Records")}: {total}
              </MDTypography>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={4000}
        onClose={closeSnackbar}
      >
        <Alert
          icon={false}
          elevation={6}
          variant="filled"
          severity={snackbar.color === "error" ? "error" : "success"}
          sx={{
            width: "100%",
            maxWidth: 360,
            borderRadius: 2,
            boxShadow: "0 6px 18px rgba(0,0,0,0.16)",
            p: 0,
            overflow: "hidden",
          }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              onClick={closeSnackbar}
              sx={{ color: "rgba(255,255,255,0.95)", mr: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1,
              background:
                snackbar.color === "error"
                  ? (theme) => theme.palette.error.main
                  : (theme) => theme.palette.success.main,
            }}
          >
            <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 700 }}>
              {snackbar.title || (snackbar.color === "error" ? "Error" : "Success")}
            </Typography>
          </Box>

          <Box sx={{ px: 2, py: 1.25, background: "transparent" }}>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.95)" }}>
              {snackbar.message}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default ExamsIndex;
